import {
  buildCompatCheckpointBody,
  compatCheckpointDocId,
  compatCheckpointStoragePath,
  validateCheckpointIntegrity,
} from './compatCheckpoint.service';
import { buildCommitKey } from './compatTypes';
import { buildApprovedBlueprintState, buildApprovedRoadmapState } from './compatPersistence.service';
import { logOriginalCompatCommit, logOriginalCompatProjectOpen } from './compatLogging.service';
import type {
  CommitChapterOutcome,
  OriginalEngineCheckpoint,
  OriginalEngineCommitResult,
  OriginalEngineCompatRepository,
  OriginalEngineProjectState,
  OriginalEngineSequelRepository,
  OriginalEngineSequelWorkspaceInput,
} from '../types';
import type { OriginalCompatCheckpointBody } from './compatTypes';

export interface CompatRepositoryStorage {
  saveCheckpointBody(uid: string, storagePath: string, body: OriginalCompatCheckpointBody): Promise<void>;
  loadCheckpointBody(storagePath: string): Promise<OriginalCompatCheckpointBody | null>;
  deleteCheckpointBody(storagePath: string): Promise<void>;
  saveChapterContent(
    uid: string,
    projectId: string,
    chapterNumber: number,
    content: string,
    title?: string,
    summary?: string,
  ): Promise<void>;
}

export interface CompatFirestoreDeps {
  uid: string;
  loadStateDoc(projectId: string): Promise<Record<string, unknown>>;
  loadProjectMeta(projectId: string): Promise<Record<string, unknown>>;
  patchStateDoc(projectId: string, data: Record<string, unknown>): Promise<void>;
  patchProjectMeta(projectId: string, data: Record<string, unknown>): Promise<void>;
  setProjectMeta(projectId: string, data: Record<string, unknown>): Promise<void>;
  setStateDoc(projectId: string, data: Record<string, unknown>): Promise<void>;
  patchCheckpointMeta(projectId: string, docId: string, data: Record<string, unknown>): Promise<void>;
  getCheckpointMeta(projectId: string, docId: string): Promise<Record<string, unknown> | null>;
  getCommitRecord(projectId: string, commitKey: string): Promise<Record<string, unknown> | null>;
  setCommitRecord(projectId: string, commitKey: string, data: Record<string, unknown>): Promise<void>;
  listProjects(): Promise<Array<{ id: string; data: Record<string, unknown> }>>;
  storage: CompatRepositoryStorage;
  stripLedger<T extends Record<string, unknown>>(ledger: T): T;
}

function mapStateToProject(projectId: string, data: Record<string, unknown>, deps: CompatFirestoreDeps): OriginalEngineProjectState {
  return {
    projectId,
    storyBible: data.storyBible as OriginalEngineProjectState['storyBible'],
    ledger: deps.stripLedger((data.ledger || {}) as Record<string, unknown>) as OriginalEngineProjectState['ledger'],
    chapters: (data.chapters || []) as OriginalEngineProjectState['chapters'],
    currentChapterNumber: (data.currentChapterNum as number) || 1,
  };
}

export function createCompatRepositoryFromDeps(deps: CompatFirestoreDeps): OriginalEngineCompatRepository {
  return {
    async loadProject(projectId) {
      const data = await deps.loadStateDoc(projectId);
      return mapStateToProject(projectId, data, deps);
    },

    async loadFullProject(projectId) {
      const [stateData, meta] = await Promise.all([
        deps.loadStateDoc(projectId),
        deps.loadProjectMeta(projectId),
      ]);
      const state = mapStateToProject(projectId, stateData, deps);
      const cp = await this.loadRecoverableCheckpoint(projectId, state.currentChapterNumber);
      const audit = {
        projectId,
        currentChapterNumber: state.currentChapterNumber,
        chapterCount: state.chapters.length,
        roadmapChapterCount: state.storyBible.chapterPlans?.length || 0,
        volume: state.storyBible.volume,
        hasRecoverableCheckpoint: Boolean(cp),
      };
      logOriginalCompatProjectOpen(audit);
      return {
        projectId,
        metadata: meta,
        storyBible: state.storyBible,
        ledger: state.ledger,
        chapters: state.chapters,
        currentChapterNumber: state.currentChapterNumber,
        volume: state.storyBible.volume,
        seriesId: state.storyBible.seriesId || (meta.seriesId as string | undefined),
        previousVolumeId: meta.previousVolumeId as string | undefined,
        entitlementState: {
          exportUnlocked: state.storyBible.exportUnlocked,
          maxChapters: state.storyBible.targetChapterCount,
        },
        recoverableCheckpoint: cp?.metadata || null,
        roadmapChapterCount: state.storyBible.chapterPlans?.length || 0,
      };
    },

    async persistBlueprintApproval(projectId, storyBible, ledger) {
      const approved = buildApprovedBlueprintState({ storyBible, ledger });
      await deps.patchStateDoc(projectId, {
        storyBible: approved.storyBible,
        ledger: deps.stripLedger(approved.ledger as Record<string, unknown>),
        lastUpdated: new Date().toISOString(),
      });
    },

    async persistRoadmapApproval(projectId, storyBible, ledger) {
      const approved = buildApprovedRoadmapState({
        storyBible,
        ledger,
        chapterPlans: storyBible.chapterPlans || [],
      });
      if (approved.validationError) throw new Error(approved.validationError);
      await deps.patchStateDoc(projectId, {
        storyBible: approved.storyBible,
        ledger: deps.stripLedger(approved.ledger as Record<string, unknown>),
        lastUpdated: new Date().toISOString(),
      });
      await deps.patchProjectMeta(projectId, {
        chapterCount: approved.storyBible.chapterPlans?.length || 0,
        lastUpdated: new Date().toISOString(),
      });
    },

    async saveStageCheckpoint(body, storagePath) {
      await deps.storage.saveCheckpointBody(deps.uid, storagePath, body);
      const { chapterDraft: _d, inspectorResult: _i, editorResult: _e, publicationResult: _p, updatedLedger: _l, loopState: _s, ...meta } = body;
      await deps.patchCheckpointMeta(body.projectId, compatCheckpointDocId(body.chapterNumber), {
        ...meta,
        storagePath,
        updatedAt: new Date().toISOString(),
      });
    },

    async loadRecoverableCheckpoint(projectId, chapterNumber) {
      const metaRaw = await deps.getCheckpointMeta(projectId, compatCheckpointDocId(chapterNumber));
      if (!metaRaw || metaRaw.deletedAt) return null;
      const metadata = metaRaw as import('../compat/compatTypes').OriginalCompatCheckpointMetadata;
      const body = metadata.storagePath
        ? await deps.storage.loadCheckpointBody(metadata.storagePath)
        : null;
      return { metadata, body };
    },

    async discardCheckpoint(projectId, chapterNumber) {
      const cp = await this.loadRecoverableCheckpoint(projectId, chapterNumber);
      if (!cp) return;
      await deps.patchCheckpointMeta(projectId, compatCheckpointDocId(chapterNumber), {
        deletedAt: new Date().toISOString(),
      });
      if (cp.metadata.storagePath) {
        await deps.storage.deleteCheckpointBody(cp.metadata.storagePath);
      }
    },

    async getCommitRecord(commitKey) {
      const [projectId] = commitKey.split(':');
      const rec = await deps.getCommitRecord(projectId, commitKey);
      if (!rec) return null;
      return { status: String(rec.status), nextChapterNumber: rec.nextChapterNumber as number | undefined };
    },

    async saveCheckpoint(checkpoint: OriginalEngineCheckpoint) {
      await deps.patchCheckpointMeta(checkpoint.projectId, `compat-${checkpoint.kind}-ch${checkpoint.chapterNumber}`, {
        ...checkpoint.payload,
        kind: checkpoint.kind,
        runId: checkpoint.runId,
        updatedAt: new Date().toISOString(),
      });
    },

    async commitChapter(result): Promise<CommitChapterOutcome> {
      const commitKey = buildCommitKey(result);
      const existing = await deps.getCommitRecord(result.projectId, commitKey);
      if (existing?.status === 'complete') {
        const audit = {
          projectId: result.projectId,
          chapterNumber: result.chapterNumber,
          runId: result.runId,
          commitKey,
          chapterStorageStatus: 'skipped' as const,
          firestoreStatus: 'skipped' as const,
          idempotentReplay: true,
          reservationAction: 'consume' as const,
        };
        logOriginalCompatCommit(audit);
        return { committed: true, idempotentReplay: true, audit };
      }

      await deps.storage.saveChapterContent(
        deps.uid,
        result.projectId,
        result.chapterNumber,
        result.chapter.content || '',
        result.chapter.title,
        result.chapter.summary,
      );
      await deps.setCommitRecord(result.projectId, commitKey, {
        status: 'storage_complete',
        chapterNumber: result.chapterNumber,
        runId: result.runId,
        updatedAt: new Date().toISOString(),
      });

      const ledger = deps.stripLedger(result.ledger as Record<string, unknown>);
      await deps.patchStateDoc(result.projectId, {
        ledger,
        chapters: result.chapters,
        currentChapterNum: result.nextChapterNumber,
        storyBible: result.storyBible,
        lastUpdated: new Date().toISOString(),
      });
      await deps.patchProjectMeta(result.projectId, {
        currentChapterNum: result.nextChapterNumber,
        chapterCount: result.chapters.length,
        lastUpdated: new Date().toISOString(),
      });
      await deps.setCommitRecord(result.projectId, commitKey, {
        status: 'complete',
        chapterNumber: result.chapterNumber,
        runId: result.runId,
        nextChapterNumber: result.nextChapterNumber,
        completedAt: new Date().toISOString(),
      });
      await this.discardCheckpoint(result.projectId, result.chapterNumber);

      const audit = {
        projectId: result.projectId,
        chapterNumber: result.chapterNumber,
        runId: result.runId,
        commitKey,
        chapterStorageStatus: 'written' as const,
        firestoreStatus: 'written' as const,
        idempotentReplay: false,
        reservationAction: 'consume' as const,
      };
      logOriginalCompatCommit(audit);
      return { committed: true, idempotentReplay: false, audit };
    },

    async retryCommitPending(result) {
      return this.commitChapter(result);
    },

    async deleteSuccessfulCheckpoint(projectId, chapterNumber) {
      await this.discardCheckpoint(projectId, chapterNumber);
    },
  };
}

export function createCompatSequelRepositoryFromDeps(
  deps: CompatFirestoreDeps,
): OriginalEngineSequelRepository {
  const base = createCompatRepositoryFromDeps(deps);
  return {
    ...base,
    async verifySourceVolumeComplete(projectId) {
      const state = await base.loadProject(projectId);
      return state.chapters.length > 0 && state.currentChapterNumber > 1;
    },
    async createSequelWorkspace(input: OriginalEngineSequelWorkspaceInput) {
      const now = new Date().toISOString();
      await deps.setProjectMeta(input.newProjectId, {
        id: input.newProjectId,
        title: input.storyBible.title,
        genre: input.storyBible.genre?.map((g) => g.name).join(', ') || '',
        lastUpdated: now,
        chapterCount: 0,
        mode: input.storyBible.mode,
        volume: input.storyBible.volume,
        seriesId: input.seriesId,
        previousVolumeId: input.previousVolumeId,
        status: 'SETUP',
        userId: deps.uid,
      });
      await deps.setStateDoc(input.newProjectId, {
        storyBible: input.storyBible,
        ledger: deps.stripLedger(input.ledger as Record<string, unknown>),
        chapters: [],
        currentChapterNum: 1,
        logs: [],
        ledgerHistory: [],
        seriesId: input.seriesId,
        previousVolumeId: input.previousVolumeId,
        volume: input.storyBible.volume,
        lastUpdated: now,
      });
    },
  };
}

export { compatCheckpointStoragePath };
